import { Injectable, OnDestroy, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { AuthSignalService } from '../state/auth.signal';
import { PushNotificationService } from '../services/push-notification.service';

/** Cambio de salud de una línea de WhatsApp reportado por Meta. */
export interface PhoneAccountHealth {
  lineaId: number;
  linea: string;
  anterior: 'OK' | 'WARN' | 'CRITICAL';
  actual: 'OK' | 'WARN' | 'CRITICAL';
  motivo: string | null;
  detalle: string | null;
  solucion: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService implements OnDestroy {
  private socket: Socket | null = null;

  private authSignal = inject(AuthSignalService);
  private router = inject(Router);
  private pushService = inject(PushNotificationService);

  // Subjects para que cualquier componente se suscriba de forma segura a los eventos globales
  private _newConversation$ = new Subject<any>();
  private _conversationClaimed$ = new Subject<any>();
  private _conversationRead$ = new Subject<{ conversationId: number; unreadCount: number }>();
  private _newMessage$ = new Subject<any>();
  private _messageReaction$ = new Subject<{ messageId: number; waMessageId: string; emoji: string }>();
  private _presenceUpdate$ = new Subject<{ onlineIds: number[] }>();
  private _zohoContactUpdated$ = new Subject<{ phone: string; conversationId: number }>();
  private _availabilityUpdate$ = new Subject<{ reason: string }>();
  private _incomingCall$ = new Subject<any>();
  private _callAnsweredElsewhere$ = new Subject<{ wacid: string; assigneeId: number }>();
  private _callEnded$ = new Subject<{ wacid: string; status: string; durationSec?: number }>();
  private _callAnswer$ = new Subject<{ wacid: string; sdp: string }>();
  private _phoneAccountHealth$ = new Subject<PhoneAccountHealth>();

  private lastUserId?: number;

  /**
   * Abre el socket. La identidad ya NO se envía desde el cliente: el backend la
   * deriva de la cookie de sesión durante el handshake. El parámetro `userId`
   * se conserva solo por compatibilidad con los llamadores existentes.
   */
  connect(userId?: number): void {
    if (userId) this.lastUserId = userId;

    // ── CRÍTICO: si el socket ya existe (incluso si aún está conectándose),
    //   NO crear otro.
    if (this.socket) return;

    this.socket = io(environment.wsUrl || environment.apiUrl.replace('/api', ''), {
      transports: ['websocket'],
      // Imprescindible: envía la cookie HttpOnly en el handshake para que el
      // servidor pueda autenticar la conexión.
      withCredentials: true,
    });

    // El servidor confirma la identidad que dedujo del JWT.
    this.socket.on('identified', (data: { userId: number }) => {
      this.lastUserId = data?.userId;
    });

    this.socket.on('connect_error', (e) => {
      if (e.message === 'unauthorized') {
        // La sesión caducó o no hay cookie válida → el socket nunca conectará.
        console.warn('[Socket] Handshake no autorizado');
        return;
      }
      console.warn('[Socket] Error:', e.message);
    });

    this.socket.on('force_logout', (data) => {
      console.warn('[Socket] Force logout received:', data?.message);
      this.disconnect();
      this.pushService.logout();
      this.authSignal.logout();
      this.router.navigate(['/login']);
    });

    // Conectar eventos globales a Subjects locales para prevenir 'pérdida de hooks'
    this.socket.on('new_conversation', (conv) => this._newConversation$.next(conv));
    this.socket.on('conversation_claimed', (conv) => this._conversationClaimed$.next(conv));
    this.socket.on('conversation_read', (payload) => this._conversationRead$.next(payload));
    this.socket.on('new_message', (msg) => this._newMessage$.next(msg));
    this.socket.on('message_reaction', (payload) => this._messageReaction$.next(payload));
    this.socket.on('presence_update', (payload: { onlineIds: number[] }) => {
      this._presenceUpdate$.next(payload);
    });
    this.socket.on('zoho_contact_updated', (payload: { phone: string; conversationId: number }) => {
      this._zohoContactUpdated$.next(payload);
    });
    this.socket.on('availability_update', (payload: { reason: string }) => {
      this._availabilityUpdate$.next(payload || { reason: 'update' });
    });

    // Salud de una línea de WhatsApp. El backend lo manda SOLO a los usuarios
    // con el permiso `phone_accounts.alerts` (ver PhoneAccountsAlertasService),
    // así que aquí no hace falta volver a filtrar por rol.
    this.socket.on('phone_account_health', (payload: PhoneAccountHealth) => {
      this._phoneAccountHealth$.next(payload);
    });

    // ── Llamadas ──────────────────────────────────────────────────────────
    // El backend las manda SOLO a los sockets del agente al que le toca
    // atender (ver ChatGateway.sendToUsers), no en broadcast: por eso no hace
    // falta filtrar aquí por destinatario.
    this.socket.on('incoming_call', (payload) => this._incomingCall$.next(payload));
    this.socket.on('call_answered_elsewhere', (payload) => this._callAnsweredElsewhere$.next(payload));
    this.socket.on('call_ended', (payload) => this._callEnded$.next(payload));
    // Saliente: el cliente descolgó y manda su SDP. Es lo que le falta al
    // navegador para abrir el audio.
    this.socket.on('call_answer', (payload) => this._callAnswer$.next(payload));
  }

  /**
   * Compatibilidad: los componentes existentes siguen llamando a identify(),
   * pero el servidor ya no acepta que el cliente declare quién es. Solo asegura
   * que el socket esté abierto.
   */
  identify(userId: number): void {
    this.lastUserId = userId;
    this.connect(userId);
  }


  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.lastUserId = undefined;
  }

  joinRoom(conversationId: number): void {
    this.socket?.emit('join_room', conversationId);
  }

  leaveRoom(conversationId: number): void {
    this.socket?.emit('leave_room', conversationId);
  }

  /** Listen for a new incoming message inside the currently open conversation */
  onNewMessage(): Observable<any> {
    return this._newMessage$.asObservable();
  }

  /** Listen for new conversations arriving (unassigned) */
  onNewConversation(): Observable<any> {
    return this._newConversation$.asObservable();
  }

  /** Listen for when any conversation gets claimed / updated */
  onConversationClaimed(): Observable<any> {
    return this._conversationClaimed$.asObservable();
  }

  /**
   * Listen for global unread state changes.
   * Payload: { conversationId: number; unreadCount: number }
   */
  onConversationRead(): Observable<{ conversationId: number; unreadCount: number }> {
    return this._conversationRead$.asObservable();
  }

  /**
   * Emite cuando un cliente de WhatsApp reacciona (emoji) a un mensaje del chat.
   * Payload: { messageId, waMessageId, emoji }
   * Si emoji === '' el cliente quitó la reacción.
   */
  onMessageReaction(): Observable<{ messageId: number; waMessageId: string; emoji: string }> {
    return this._messageReaction$.asObservable();
  }

  /**
   * Emite cuando cambia la presencia de usuarios (online/offline).
   * Payload: { onlineIds: number[] }
   */
  onPresenceUpdate(): Observable<{ onlineIds: number[] }> {
    return this._presenceUpdate$.asObservable();
  }

  /**
   * Emite cuando la info de Zoho de un contacto cambió (lead creado/actualizado,
   * Propietario_de_chat reasignado) — para que el panel CRM recargue.
   * Payload: { phone, conversationId }
   */
  onZohoContactUpdated(): Observable<{ phone: string; conversationId: number }> {
    return this._zohoContactUpdated$.asObservable();
  }

  /**
   * Emite cuando cambia la disponibilidad propia del agente (fin de descanso/almuerzo,
   * salida automática, etc.). El widget re-consulta GET /availability/me al recibirlo.
   */
  onAvailabilityUpdate(): Observable<{ reason: string }> {
    return this._availabilityUpdate$.asObservable();
  }

  /**
   * Entra una llamada para este agente: hay que abrir el modal y timbrar.
   * El payload trae la oferta SDP del cliente, que el navegador necesita para
   * montar su RTCPeerConnection.
   */
  onIncomingCall(): Observable<any> {
    return this._incomingCall$.asObservable();
  }

  /** Otro agente (u otra pestaña) cogió la llamada: cerrar el modal sin ruido. */
  onCallAnsweredElsewhere(): Observable<{ wacid: string; assigneeId: number }> {
    return this._callAnsweredElsewhere$.asObservable();
  }

  /** La llamada terminó (colgada, perdida, rechazada o fallida). */
  onCallEnded(): Observable<{ wacid: string; status: string; durationSec?: number }> {
    return this._callEnded$.asObservable();
  }

  /** En una llamada NUESTRA, el cliente descolgó: llega su respuesta SDP. */
  onCallAnswer(): Observable<{ wacid: string; sdp: string }> {
    return this._callAnswer$.asObservable();
  }

  /**
   * Meta cambió el estado de salud de una línea (calidad, marcado, restricción).
   * Solo llega a quien tiene el permiso `phone_accounts.alerts`.
   */
  onPhoneAccountHealth(): Observable<PhoneAccountHealth> {
    return this._phoneAccountHealth$.asObservable();
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}

